import React from 'react';
import { useAccountTransactionListApi } from '@hero/api-hooks';
import type { AccountTransactionGetCurrentUserAPI } from '@hero/api';
import { AccountTransactionList } from '../List/AccountTransactionList';

export interface AccountTransactionListManagerProps {
  /**
   * Extra query merged into the list call. The CRM Invoices tab
   * passes `{ accountId }`; the InvoiceDrawer Transactions tab
   * passes `{ invoiceId }`. Without it the list scopes to the
   * viewer's full visible set (every row across every account).
   */
  scopeQuery?: Partial<AccountTransactionGetCurrentUserAPI['req']>;
  className?: string;
}

/**
 * Self-contained `AccountTransaction` list — owns its own infinite
 * query and renders the card list. Mirrors `TransactionList` +
 * `useTransactionListApi` glue used by the legacy
 * `TransactionTableManager`, scoped down for the cases the
 * bilateral model actually needs:
 *
 *   - Per-account scope (CRM Invoices tab → `AccountPage`).
 *   - Per-invoice scope (InvoiceDrawer → Transactions tab).
 *
 * Status / type filters live on the parent surface for now — the
 * three scopes above are narrow enough that an extra filter row
 * would be noise. We can lift one in here later if the per-account
 * row count makes the list unscannable.
 */
export const AccountTransactionListManager: React.FC<AccountTransactionListManagerProps> = ({
  scopeQuery,
  className,
}) => {
  const query = useAccountTransactionListApi(scopeQuery ?? {});

  return <AccountTransactionList query={query} className={className} />;
};
